import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Play, Pause, RotateCcw, Users, Zap, ArrowRight, Loader2, Edit3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TRIGGER_META = {
  welcome: { color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30", label: "New lead" },
  quote_abandoner: { color: "bg-amber-500/10 text-amber-600 border-amber-500/30", label: "Quote abandoner" },
  plan_comparison: { color: "bg-sky-500/10 text-sky-600 border-sky-500/30", label: "Comparison visitor" },
  post_signup: { color: "bg-primary/10 text-primary border-primary/30", label: "Post-signup" },
  expiry_warning: { color: "bg-rose-500/10 text-rose-600 border-rose-500/30", label: "Expiry warning" },
};

export default function AdminDripSequences() {
  const [tab, setTab] = useState("sequences");
  const [seqs, setSeqs] = useState([]);
  const [stats, setStats] = useState(null);
  const [enrolments, setEnrolments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", steps: [], enabled: true });
  const [dispatching, setDispatching] = useState(false);

  const loadSeqs = useCallback(async () => {
    try { const r = await api.get("/drip/sequences"); setSeqs(r.data || []); }
    catch { toast.error("Failed to load sequences"); }
  }, []);
  const loadStats = useCallback(async () => {
    try { const r = await api.get("/drip/stats"); setStats(r.data); } catch {}
  }, []);
  const loadEnr = useCallback(async () => {
    try { const r = await api.get("/drip/enrolments", { params: { limit: 200 } }); setEnrolments(r.data || []); }
    catch { toast.error("Failed to load enrolments"); }
  }, []);

  useEffect(() => { loadSeqs(); loadStats(); }, [loadSeqs, loadStats]);
  useEffect(() => { if (tab === "enrolments") loadEnr(); }, [tab, loadEnr]);

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, steps: s.steps || [], enabled: s.enabled });
  };
  const saveEdit = async () => {
    try {
      await api.put(`/drip/sequences/${editing.id}`, form);
      toast.success("Sequence saved");
      setEditing(null); loadSeqs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const toggle = async (s) => {
    try { await api.put(`/drip/sequences/${s.id}`, { enabled: !s.enabled }); loadSeqs(); }
    catch { toast.error("Toggle failed"); }
  };
  const dispatch = async () => {
    setDispatching(true);
    try {
      const r = await api.post("/drip/dispatch", null, { params: { limit: 100 } });
      toast.success(`Dispatched: ${r.data.sent} sent · ${r.data.skipped} skipped · ${r.data.failed} failed (${r.data.due} due)`);
      loadStats(); loadEnr();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dispatch failed"); }
    finally { setDispatching(false); }
  };

  const updateStep = (idx, field, val) => {
    const next = [...form.steps];
    next[idx] = { ...next[idx], [field]: field === "delay_hours" ? Number(val) : val };
    setForm({ ...form, steps: next });
  };
  const addStep = () => setForm({ ...form, steps: [...form.steps, { delay_hours: 24, subject: "New email", body: "<p>Body…</p>" }] });
  const removeStep = (idx) => setForm({ ...form, steps: form.steps.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-6" data-testid="admin-drip-engine">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline text-secondary">Admin · Drip Engine</div>
          <h1 className="mt-1 font-display text-4xl font-light tracking-tight flex items-center gap-3">
            <Send className="w-7 h-7 text-primary" /> Email Drip Sequences
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Multi-step Resend sequences triggered by user events. Auto-enrolled on lead capture, quote submit, and signup.
          </p>
        </div>
        <Button onClick={dispatch} disabled={dispatching} data-testid="drip-dispatch-btn">
          {dispatching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
          Dispatch due emails
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Sequences" value={stats.sequences} />
          <Tile label="Active enrolments" value={stats.active} color="text-emerald-600" />
          <Tile label="Completed" value={stats.completed} />
          <Tile label="Paused" value={stats.paused} color="text-amber-600" />
        </div>
      )}

      <div className="border-b border-border">
        <nav className="flex gap-1">
          {[{ k: "sequences", label: "Sequences" }, { k: "enrolments", label: "Enrolments" }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`drip-tab-${t.k}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === "sequences" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {seqs.map((s) => (
            <div key={s.id} data-testid={`drip-card-${s.trigger}`}
              className="rounded-2xl border border-border bg-card p-5 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Badge className={TRIGGER_META[s.trigger]?.color}>{TRIGGER_META[s.trigger]?.label || s.trigger}</Badge>
                  <h3 className="mt-2 font-display text-lg">{s.name}</h3>
                </div>
                <Button size="icon" variant="ghost" onClick={() => toggle(s)}
                  data-testid={`drip-toggle-${s.trigger}`}>
                  {s.enabled ? <Play className="w-4 h-4 text-emerald-600" /> : <Pause className="w-4 h-4 text-muted-foreground" />}
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center pt-1">
                <MiniStat label="Steps" value={s.step_count} />
                <MiniStat label="Active" value={s.enrolled_active} />
                <MiniStat label="Done" value={s.enrolled_completed} />
              </div>
              <div className="text-xs text-muted-foreground">
                Timing: {(s.steps || []).map(x => `${x.delay_hours}h`).join(" → ")}
              </div>
              <Button size="sm" variant="outline" className="w-full" onClick={() => openEdit(s)}
                data-testid={`drip-edit-${s.trigger}`}>
                <Edit3 className="w-3 h-3 mr-2" /> Edit sequence
              </Button>
            </div>
          ))}
        </div>
      )}

      {tab === "enrolments" && (
        <div className="rounded-2xl border border-border bg-card divide-y divide-border">
          {enrolments.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No enrolments yet.</div>}
          {enrolments.map((e) => (
            <div key={e.id} className="p-4 flex items-center gap-4 text-sm" data-testid={`enr-${e.id}`}>
              <Badge className={TRIGGER_META[e.trigger]?.color}>{TRIGGER_META[e.trigger]?.label || e.trigger}</Badge>
              <div className="flex-1 min-w-0">
                <div className="truncate">{e.email}</div>
                <div className="text-xs text-muted-foreground">Step {e.current_step} · {e.sends} sends · {e.status}</div>
              </div>
              <div className="text-xs text-muted-foreground">{new Date(e.enrolled_at).toLocaleString("en-GB")}</div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit sequence: {editing?.name}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                Enabled
              </label>
              <div className="space-y-3">
                <Label className="text-xs">Steps ({form.steps.length})</Label>
                {form.steps.map((s, i) => (
                  <div key={i} className="rounded-lg border border-border p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-14">Step {i + 1}</span>
                      <Input type="number" value={s.delay_hours} onChange={(e) => updateStep(i, "delay_hours", e.target.value)}
                        className="w-24" placeholder="hrs" />
                      <span className="text-xs text-muted-foreground">hours after previous</span>
                      <Button size="sm" variant="ghost" className="ml-auto text-destructive" onClick={() => removeStep(i)}>Remove</Button>
                    </div>
                    <Input value={s.subject} onChange={(e) => updateStep(i, "subject", e.target.value)} placeholder="Subject" />
                    <Textarea rows={5} value={s.body} onChange={(e) => updateStep(i, "body", e.target.value)}
                      className="font-mono text-xs" placeholder="<p>HTML body — use {{name}} for personalization</p>" />
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addStep}>+ Add step</Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tile({ label, value, color = "" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-3xl font-semibold ${color}`}>{value ?? 0}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg bg-muted/40 p-2">
      <div className="text-lg font-semibold">{value ?? 0}</div>
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
    </div>
  );
}
